import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ProductForm from '../../components/inventory/ProductForm';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  addProduct,
  getProductCategories,
  getProductBrands,
  generateBarcode,
  validateBarcodeUnique
} from '../../services/inventoryService';

const AddProductPage = () => {
  const navigate = useNavigate();
  const { 
    currentUser, 
    vendorProfile, 
    getAllCategories, 
    getAllBrands, 
    addUserCategory, 
    addUserBrand 
  } = useAuth();
  const { showAlert } = useNotification();

  const [systemCategories, setSystemCategories] = useState([]);
  const [systemBrands, setSystemBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Get user-created categories and brands from AuthContext
  const userCategories = getAllCategories();
  const userBrands = getAllBrands();

  // Combine system and user-created data
  const allCategories = [...systemCategories, ...userCategories];
  const allBrands = [...systemBrands, ...userBrands];

  // Updated initial product data
  const initialProductData = {
    name: '',
    partNumber: '',
    description: '',
    sku: '',
    barcode: generateBarcode(),
    price: '',
    sellingPrice: '',
    comparePrice: '',
    cost: '',
    stock: '',
    reorderLevel: '10',
    lowStockThreshold: '10',
    categories: [],
    brands: [],
    modelCompatibility: [],
    weight: '',
    dimensions: {
      length: '',
      width: '',
      height: ''
    },
    supplierName: '',
    purchaseDate: '',
    invoiceNo: '',
    location: '',
    hsnCode: '',
    warrantyMonths: '',
    lastUsedDate: '',
    status: 'Active',
    notes: '',
    images: [],
    specifications: [],
    isActive: true
  };

  // Load system categories and brands
  useEffect(() => {
    const loadSystemCategoriesAndBrands = async () => {
      try {
        setLoading(true);

        // Get all categories and filter out user-created ones for system categories
        const allCategoriesResult = await getProductCategories();
        const systemCats = allCategoriesResult.filter(cat => !cat.isUserCreated);
        setSystemCategories(systemCats);

        // Get all brands and filter out user-created ones for system brands
        const allBrandsResult = await getProductBrands();
        const systemBrands = allBrandsResult.filter(brand => !brand.isUserCreated);
        setSystemBrands(systemBrands);

        setLoading(false);
      } catch (error) {
        console.error('Error loading system categories and brands:', error);
        setError('Failed to load categories and brands. Please try again.');
        setLoading(false);
      }
    };

    loadSystemCategoriesAndBrands();
  }, []);

  // Handle adding new category (will be user-specific)
  const handleAddCategory = async (categoryData) => {
    try {
      console.log('Adding new category:', categoryData);
      
      if (!currentUser) {
        throw new Error('You must be logged in to add categories');
      }

      const newCategory = await addUserCategory(categoryData);
      showAlert('Category added successfully!', 'success');
      return newCategory;
    } catch (error) {
      console.error('Error adding category:', error);
      showAlert(error.message || 'Failed to add category. Please try again.', 'error');
      throw error;
    }
  };

  // Handle adding new brand (will be user-specific)
  const handleAddBrand = async (brandData) => {
    try {
      console.log('Adding new brand:', brandData);
      
      if (!currentUser) {
        throw new Error('You must be logged in to add brands');
      }

      const newBrand = await addUserBrand(brandData);
      showAlert('Brand added successfully!', 'success');
      return newBrand;
    } catch (error) {
      console.error('Error adding brand:', error);
      showAlert(error.message || 'Failed to add brand. Please try again.', 'error');
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (productData) => {
    try {
      if (!vendorProfile?.uid) {
        throw new Error('Vendor profile not found');
      }

      setSubmitting(true);
      setError('');

      // Validate barcode uniqueness if provided
      if (productData.barcode) {
        const isBarcodeUnique = await validateBarcodeUnique(productData.barcode);
        if (!isBarcodeUnique) {
          showAlert('Barcode already exists. Please use a different barcode.', 'error');
          setSubmitting(false);
          return;
        }
      }

      // Process the product data
      const processedData = {
        ...productData,
        vendorId: vendorProfile.uid,
        sellingPrice: productData.price || productData.sellingPrice,
        stock: productData.stock ? parseInt(productData.stock, 10) : 0,
        reorderLevel: productData.reorderLevel ? parseInt(productData.reorderLevel, 10) : 10,
        lowStockThreshold: productData.lowStockThreshold || productData.reorderLevel ? parseInt(productData.lowStockThreshold || productData.reorderLevel, 10) : 10,
        price: productData.price ? parseFloat(productData.price) : 0,
        comparePrice: productData.comparePrice ? parseFloat(productData.comparePrice) : null,
        cost: productData.cost ? parseFloat(productData.cost) : null,
        weight: productData.weight ? parseFloat(productData.weight) : null,
        warrantyMonths: productData.warrantyMonths ? parseInt(productData.warrantyMonths, 10) : null,
        dimensions: {
          length: productData.dimensions?.length ? parseFloat(productData.dimensions.length) : null,
          width: productData.dimensions?.width ? parseFloat(productData.dimensions.width) : null,
          height: productData.dimensions?.height ? parseFloat(productData.dimensions.height) : null
        },
        purchaseDate: productData.purchaseDate || null,
        lastUsedDate: productData.lastUsedDate || null,
        barcode: productData.barcode || generateBarcode(),
        brands: Array.isArray(productData.brands) ? productData.brands : (productData.brand ? [productData.brand] : []),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Remove old brand field if it exists
      if (processedData.brand) {
        delete processedData.brand;
      }

      console.log('Submitting product data:', processedData);

      // Add product
      await addProduct(processedData);

      showAlert('Automotive spare part added successfully!', 'success');
      navigate('/inventory');
    } catch (error) {
      console.error('Error adding product:', error);
      setError(error.message || 'Failed to add product. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-6">
          <div className="mb-8">
            <div className="mb-8 flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-150"
                aria-label="Go back to previous page"
              >
                <svg
                  className="w-5 h-5 mr-2 -ml-1"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              Add New Automotive Spare Part
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Add a new automotive spare part to your inventory with barcode generation, part numbers, model compatibility, and supplier information.
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <ProductForm
            initialData={initialProductData}
            categories={allCategories}
            brands={allBrands}
            loading={loading}
            submitting={submitting}
            onSubmit={handleSubmit}
            onAddCategory={handleAddCategory}
            onAddBrand={handleAddBrand}
            submitButtonText="Add Spare Part"
            isEdit={false}
            isMainAdmin={vendorProfile?.isMainAdmin === true}  // ADD THIS
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddProductPage;